import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router"
import { CommandDialog, CommandEmpty, CommandInput, CommandList } from "@/components/ui/command"
import { DialogTitle } from "@/components/ui/dialog"
import { FileText, FolderOpen, Loader2, Globe, FileStack } from "lucide-react"
import { cn } from "@/lib/utils"
import { useTranslations } from "@/hooks/useTranslations"
import { getAuthHeaders, toWafSafeJsonBody } from "@/lib/apiUtils"

interface SearchResult {
	id: string
	entityType: string
	entityId: string
	orgId: string | null
	preview: string
	metadata: Record<string, unknown>
	similarity: number
}

const TAB_IDS = ["mine", "public", "templates"] as const

const ENTITY_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
	expedient: FolderOpen,
	report: FileText,
	section: FileText,
	template: FileStack,
	public_tender: Globe,
}

function buildRoute(r: SearchResult): string {
	switch (r.entityType) {
		case "expedient": return `/expedients/${r.entityId}`
		case "report": return `/reports/${r.entityId}`
		case "section": {
			const reportId = (r.metadata?.reportId as string) ?? ""
			return reportId ? `/reports/${reportId}` : "/"
		}
		case "template": return `/procurement-templates/${r.entityId}`
		case "public_tender": return `/public-tenders/${r.entityId}`
		default: return "/"
	}
}

function getTitle(r: SearchResult): string {
	return (r.metadata?.title as string) || r.preview.split("\n")[0] || r.entityId
}

function useDebouncedValue<T>(value: T, delay: number): T {
	const [v, setV] = useState(value)
	useEffect(() => {
		const h = setTimeout(() => setV(value), delay)
		return () => clearTimeout(h)
	}, [value, delay])
	return v
}

export function CommandPalette({
	open,
	onOpenChange,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
}) {
	const navigate = useNavigate()
	const t = useTranslations()
	const [query, setQuery] = useState("")
	const [tab, setTab] = useState<"mine" | "public" | "templates">("mine")
	const [results, setResults] = useState<SearchResult[]>([])
	const [loading, setLoading] = useState(false)
	const debounced = useDebouncedValue(query, 300)
	const reqIdRef = useRef(0)

	const runSearch = useCallback(async (q: string, currentTab: string) => {
		if (!q.trim() || q.trim().length < 2) {
			setResults([])
			setLoading(false)
			return
		}
		setLoading(true)
		const reqId = ++reqIdRef.current
		try {
			const res = await fetch("/api/search", {
				method: "POST",
				headers: await getAuthHeaders(),
				body: toWafSafeJsonBody({ query: q.trim(), tab: currentTab, limit: 15 }, { wafSafe: true }),
			})
			if (!res.ok) throw new Error("Error")
			const data = await res.json()
			if (reqId === reqIdRef.current) {
				setResults(data.results ?? [])
			}
		} catch {
			if (reqId === reqIdRef.current) setResults([])
		} finally {
			if (reqId === reqIdRef.current) setLoading(false)
		}
	}, [])

	useEffect(() => { runSearch(debounced, tab) }, [debounced, tab, runSearch])

	// Reset state on close
	useEffect(() => {
		if (!open) {
			setQuery("")
			setResults([])
			setTab("mine")
		}
	}, [open])

	const handleSelect = (r: SearchResult) => {
		const route = buildRoute(r)
		if (route !== "/") {
			navigate(route)
			onOpenChange(false)
		}
	}

	const groupedResults = useMemo(() => {
		// Single list already grouped by relevance; keep as-is for now
		return results
	}, [results])

	return (
		<CommandDialog open={open} onOpenChange={onOpenChange}>
			<DialogTitle className="sr-only">{t("commandPalette.title")}</DialogTitle>
			<CommandInput
				placeholder={t("commandPalette.placeholder")}
				value={query}
				onValueChange={setQuery}
			/>

			{/* Tabs */}
			<div className="flex items-center gap-1 border-b px-3 py-2">
				{TAB_IDS.map((id) => (
					<button
						key={id}
						type="button"
						onClick={() => setTab(id)}
						className={cn(
							"rounded-md px-3 py-1 text-xs font-medium transition-colors",
							tab === id
								? "bg-primary text-primary-foreground"
								: "text-muted-foreground hover:bg-muted"
						)}
					>
						{t(`commandPalette.tabs.${id}`)}
					</button>
				))}
				{loading && <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin text-muted-foreground" />}
			</div>

			<CommandList className="max-h-[60vh]">
				{!loading && query.trim().length >= 2 && results.length === 0 && (
					<CommandEmpty>{t("commandPalette.noResults")}</CommandEmpty>
				)}
				{query.trim().length < 2 && (
					<div className="py-8 text-center text-sm text-muted-foreground">
						{t("commandPalette.minChars")}
					</div>
				)}
				{groupedResults.map((r) => {
					const Icon = ENTITY_ICON[r.entityType] ?? FileText
					const title = getTitle(r)
					const preview = r.preview.split("\n").slice(1).join(" ").slice(0, 120)
					return (
						<button
							key={r.id}
							type="button"
							onClick={() => handleSelect(r)}
							className="flex w-full items-start gap-3 px-3 py-2.5 text-left hover:bg-muted transition-colors border-b last:border-b-0"
						>
							<Icon className="h-4 w-4 mt-1 text-muted-foreground shrink-0" />
							<div className="min-w-0 flex-1">
								<p className="text-sm font-medium truncate">{title}</p>
								{preview && (
									<p className="text-xs text-muted-foreground truncate">{preview}</p>
								)}
							</div>
							<span className="text-[10px] text-muted-foreground tabular-nums shrink-0 mt-1">
								{(r.similarity * 100).toFixed(0)}%
							</span>
						</button>
					)
				})}
			</CommandList>
		</CommandDialog>
	)
}
